import styles from "./notificationsListModal.module.scss";
import {observer} from "mobx-react-lite";
import React, {useEffect, useRef, useState} from "react";
import moment from "moment";
import Modal from "@components/UI/modal/modal";
import Loader from "@components/UI/loader/loader";
import useFetching from "@source/hooks/useFetching";
import NotificationController from "@source/api/controllers/notificationController";
import {DEFAULT_NOTIFICATIONS_LIMIT} from "@source/api/models/notificationModels";
import notificationsStore from "@store/notificationsStore";

const NotificationsListModal = observer(() => {
  const [page, setPage] = useState(0);
  const requestedPage = useRef(0);
  const [getNotifications, isLoading] = useFetching<ReturnType<typeof NotificationController.getNotifications>>(NotificationController.getNotifications);
  const [getUnreadCount] = useFetching<ReturnType<typeof NotificationController.getUnreadCount>>(NotificationController.getUnreadCount);

  useEffect(() => {
    if (notificationsStore.listOpen) {
      return;
    }

    setPage(0);
    requestedPage.current = 0;
  }, [notificationsStore.listOpen]);

  useEffect(() => {
    if (!notificationsStore.listOpen) {
      return;
    }

    let cancelled = false;

    const loadNotifications = async () => {
      const notifications = await getNotifications({
        page,
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
      });

      if (cancelled || !Array.isArray(notifications)) {
        return;
      }

      if (page) {
        notificationsStore.addNotifications(notifications);
      } else {
        notificationsStore.setNotifications(notifications);
      }

      const unreadCount = await getUnreadCount();
      if (cancelled || !unreadCount) {
        return;
      }

      notificationsStore.setUnreadCount(unreadCount.count);
    };

    loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [notificationsStore.listOpen, page]);

  const onClose = () => {
    notificationsStore.closeList();
  };

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isNearEnd = target.scrollHeight - (target.scrollTop + target.clientHeight) < 100;

    const nextPage = Math.ceil(notificationsStore.notifications.length / DEFAULT_NOTIFICATIONS_LIMIT);

    if (isNearEnd && !isLoading && notificationsStore.hasMore && notificationsStore.notifications.length && nextPage > page && requestedPage.current !== nextPage) {
      requestedPage.current = nextPage;
      setPage(nextPage);
    }
  };

  return (
    <Modal open={notificationsStore.listOpen} close={onClose} className={styles.modal} maxWidth={560}>
      <div className={styles.content}>
        <span className={styles.title}>Notifications</span>

        <div className={styles.list} onScroll={onScroll}>
          {notificationsStore.notifications.map((notification) => (
            <div className={styles.item} key={notification._id}>
              <span className={styles.itemTitle}>{notification.title}</span>
              <span className={styles.body}>{notification.body}</span>
              <span className={styles.date}>
                {moment(notification.createdAt).isValid()
                  ? `${moment(notification.createdAt).format("DD MMM YYYY")} at ${moment(notification.createdAt).format("hh:mm A")}`
                  : ""}
              </span>
            </div>
          ))}

          {isLoading ?
            <Loader className={styles.loader} width={30} height={30}/>
            :
            null}

          {!isLoading && !notificationsStore.notifications.length ?
            <span className={styles.empty}>No notifications</span>
            :
            null}
        </div>
      </div>
    </Modal>
  );
});

export default NotificationsListModal;
